import { useEffect, useRef } from 'react';

import { bridgeAtom, InitState, useValue, widgetPopupsStatesAtom } from '@/store';

import { PopupState, tooltipConfig, WidgetPopupType } from '@/modules/models';

import { usePausableTimeout } from './usePausableTimeout';

interface UseInitPopupProps {
  isDisabled: boolean;
  isWidgetHidden: boolean;
  isHover: boolean;
  setPopupState: (popupType: WidgetPopupType, state: PopupState) => void;
  startPopupWithDelay: (popupType: WidgetPopupType, delay: number) => void;
  hideTooltip: () => void;
}

const DEFAULT_DURATION = 5000;
const DEFAULT_DELAY = 4000;
export const useInitPopup = ({
  isDisabled,
  isWidgetHidden,
  startPopupWithDelay,
  setPopupState,
  hideTooltip,
  isHover,
}: UseInitPopupProps) => {
  const shownRef = useRef(false);

  const { pageType, widget } = useValue(bridgeAtom);
  const { initTooltip } = widget || {};
  const widgetPopupsStates = useValue(widgetPopupsStatesAtom);
  const { INIT, INIT_WITH_CLOSE_BTN } = widgetPopupsStates;
  const state = INIT?.[pageType as keyof InitState];

  const delay = (initTooltip as tooltipConfig)?.delay ?? DEFAULT_DELAY;
  const duration = (initTooltip as tooltipConfig)?.duration ?? DEFAULT_DURATION;
  const pauseOnHover = (initTooltip as tooltipConfig)?.pauseOnHover;

  const { start } = usePausableTimeout(isHover, pauseOnHover, hideTooltip);

  useEffect(() => {
    if (
      isDisabled ||
      INIT_WITH_CLOSE_BTN !== PopupState.CLOSED ||
      state !== PopupState.IDLE ||
      isWidgetHidden ||
      !initTooltip
    ) {
      return;
    }

    setPopupState(WidgetPopupType.INIT, PopupState.STARTED);
  }, [isDisabled, state, INIT_WITH_CLOSE_BTN, setPopupState, initTooltip, isWidgetHidden]);

  useEffect(() => {
    if (state !== PopupState.STARTED || shownRef.current) {
      return;
    }

    shownRef.current = true;
    startPopupWithDelay(WidgetPopupType.INIT, delay);
  }, [state, startPopupWithDelay, delay]);

  useEffect(() => {
    if (state !== PopupState.VISIBLE || isDisabled) {
      return;
    }

    start(duration);
    setPopupState(WidgetPopupType.INIT, PopupState.SEEN);
  }, [isDisabled, state, duration, start, setPopupState]);

  return null;
};
